// FormBuilder.jsx — Milestone 2's rendered, editable form builder (§6).
//
// The draft rendered as the form it would become: real controls per type,
// grouped by fieldset in sortPosition order. Everything is editable HERE
// because nothing is immutable YET — fieldType, fieldset, and module scope
// are permanent only after creation in FMX, so those choices carry an amber
// "permanent after creation" treatment while everything else gets a quiet
// "editable later in FMX" hint. No modal interruptions on this screen.
//
// Alongside the form: the needs-attention strip (low confidence, flags,
// couldn't-interpret — with jump links), the buckets panel (dismiss or
// promote every item), and the page-level source panel (Decision #13).
// Reorder is up/down buttons (Decision #17 — drag-and-drop is increment 2);
// there is no re-extract button yet (same decision).
//
// Every edit autosaves through onDraftChange (debounced upstream) — closing
// the tab loses nothing; the session resumes from the sessions list. The
// only exit forward is "Continue to dry run", and navigation between the
// builder and the dry run is free in both directions (Decision #15).

import { useMemo, useRef, useState } from 'react';
import { T } from '../../theme';
import { Button, Badge } from '../fmxkit';
import { fieldsetLabel, normalizeFieldset } from '../../fieldBuilder/fieldsets';
import { FIELD_TYPES } from '../../fieldBuilder/fmxCustomFieldsApi';
import { screenAgainstExisting, findBatchNameCollisions } from '../../fieldBuilder/dedupe';
import {
  updateField, changeFieldType, changeFieldset, moveField, moveFieldToPosition,
  addField, deleteField, revertField, extractedVersionOf, fieldsInFieldset,
  dismissBucketItem, restoreBucketItem, promoteBucketItem, bucketItems,
  mergeFieldsInto, splitField, setVisibilityFormula,
} from '../../fieldBuilder/draftOps';
import { formulaChecklist } from '../../fieldBuilder/visibilityFormula';
import { composePrereq, liveNamesByPrereqKind } from '../../fieldBuilder/prereqs';
import FieldControlPreview from './FormControls';
import BucketsPanel from './BucketsPanel';
import SourcePanel from './SourcePanel';
import CopyFormulaButton from './CopyFormulaButton';

const CONF_BADGE = { high: 'green', medium: 'orange', low: 'red' };
const PROVENANCE = {
  ai:     { label: 'AI proposed', bg: T.tint.info,    fg: T.brand },
  edited: { label: 'Edited',      bg: T.tint.warning, fg: T.orangeText },
  added:  { label: 'Added',       bg: T.tint.success, fg: T.greenText },
};

// The amber treatment for the creation-permanent attributes.
function PermanentChip() {
  return (
    <span style={{
      font: `700 9px/13px ${T.fontSans}`, color: T.orangeText, background: T.tint.warning,
      border: `1px solid ${T.badge.orange}`, borderRadius: T.radiusPill, padding: '1px 7px',
      textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
    }}>
      Permanent after creation
    </span>
  );
}
function EditableLaterHint() {
  return <span style={{ font: `400 10px/14px ${T.fontSans}`, color: T.inkSoft }}>Editable later in FMX</span>;
}

// Click-to-edit text: a label that becomes an input on click, commits on
// blur/Enter, cancels on Escape.
function InlineText({ value, placeholder, onCommit, textStyle, multiline = false, inputStyle }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const commit = () => { setEditing(false); if (text !== value) onCommit(text); };
  if (!editing) {
    return (
      <span
        onClick={() => { setText(value || ''); setEditing(true); }}
        title="Click to edit"
        style={{ cursor: 'text', borderBottom: `1px dashed ${T.border}`, ...(value ? {} : { color: T.inkSoft, fontStyle: 'italic' }), ...textStyle }}
      >
        {value || placeholder}
      </span>
    );
  }
  const shared = {
    autoFocus: true,
    value: text,
    onChange: e => setText(e.target.value),
    onBlur: commit,
    onKeyDown: e => {
      if (e.key === 'Enter' && !multiline) commit();
      if (e.key === 'Escape') setEditing(false);
    },
  };
  return multiline
    ? <textarea className="fmx-textarea" rows={2} {...shared} style={inputStyle} />
    : <input className="fmx-input" {...shared} style={{ minWidth: 220, ...inputStyle }} />;
}

// ── Options editor (dropdowns) ──────────────────────────────────────────────

function OptionsEditor({ field, onChange }) {
  const [newOpt, setNewOpt] = useState('');
  const options = field.options || [];
  const defaults = field.defaults || [];

  const setOptions = (nextOptions, nextDefaults = defaults) =>
    onChange({ options: nextOptions, defaults: nextDefaults.filter(d => nextOptions.includes(d)) });

  const editOption = (i, text) => {
    const next = [...options];
    const old = next[i];
    next[i] = text;
    // A renamed option keeps its default status.
    setOptions(next, defaults.map(d => (d === old ? text : d)));
  };
  const removeOption = (i) => setOptions(options.filter((_, x) => x !== i));
  const moveOption = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= options.length) return;
    const next = [...options];
    [next[i], next[to]] = [next[to], next[i]];
    setOptions(next);
  };
  const toggleDefault = (opt) => {
    const has = defaults.includes(opt);
    if (has) onChange({ defaults: defaults.filter(d => d !== opt) });
    else onChange({ defaults: field.allowMultipleSelections ? [...defaults, opt] : [opt] });
  };
  const add = () => {
    const text = newOpt.trim();
    if (!text) return;
    setOptions([...options, text]);
    setNewOpt('');
  };

  return (
    <div>
      <div style={{ font: `600 11px/16px ${T.fontSans}`, color: T.inkMuted, marginBottom: 4 }}>
        Options <span style={{ fontWeight: 400, color: T.inkSoft }}>(star = default)</span>
      </div>
      {options.map((opt, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <button
            className="fmx-btn-xs" title={defaults.includes(opt) ? 'Remove default' : 'Make default'}
            onClick={() => toggleDefault(opt)}
            style={{ color: defaults.includes(opt) ? T.orange500 : T.inkSoft, border: 'none', background: 'none', padding: 2 }}
          >
            <i className={`fa-${defaults.includes(opt) ? 'solid' : 'regular'} fa-star`} />
          </button>
          <InlineText
            value={opt}
            placeholder="(empty option)"
            onCommit={text => editOption(i, text)}
            textStyle={{ font: `400 12px/17px ${T.fontSans}`, color: T.ink, flex: 1 }}
            inputStyle={{ flex: 1, fontSize: 12, padding: '3px 8px' }}
          />
          <span style={{ flex: 1 }} />
          <button className="fmx-btn-xs" title="Move up" disabled={i === 0} onClick={() => moveOption(i, -1)} style={{ opacity: i === 0 ? 0.35 : 1, padding: '2px 6px' }}>
            <i className="fa-solid fa-chevron-up" />
          </button>
          <button className="fmx-btn-xs" title="Move down" disabled={i === options.length - 1} onClick={() => moveOption(i, 1)} style={{ opacity: i === options.length - 1 ? 0.35 : 1, padding: '2px 6px' }}>
            <i className="fa-solid fa-chevron-down" />
          </button>
          <button className="fmx-btn-xs" title="Remove option" onClick={() => removeOption(i)} style={{ padding: '2px 6px' }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
        <input
          className="fmx-input"
          style={{ flex: 1, fontSize: 12, padding: '4px 8px' }}
          placeholder="Add an option…"
          value={newOpt}
          onChange={e => setNewOpt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
        />
        <button className="fmx-btn-xs" onClick={add}><i className="fa-solid fa-plus" /> Add</button>
      </div>
    </div>
  );
}

// The field's 1-based position within its fieldset group, editable by
// typing — committing a number moves the field there and every other field
// in the group renumbers (out-of-range clamps to the ends).
function PositionInput({ index, size, onMoveTo }) {
  const [text, setText] = useState(null); // null = displaying, not editing
  const commit = () => {
    const n = parseInt(text, 10);
    setText(null);
    if (!Number.isNaN(n) && n - 1 !== index) onMoveTo(n - 1);
  };
  return (
    <input
      className="fmx-input"
      title={`Position in this fieldset (1–${size}) — type a number to move the field`}
      style={{ width: 34, padding: '3px 2px', textAlign: 'center', fontSize: 11, fontFamily: T.fontData }}
      value={text ?? String(index + 1)}
      onFocus={e => { setText(String(index + 1)); e.target.select(); }}
      onChange={e => setText(e.target.value.replace(/[^\d]/g, ''))}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') setText(null);
      }}
    />
  );
}

// ── One field card ──────────────────────────────────────────────────────────

function Toggle({ checked, onChange, label, hint }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, font: `400 12px/17px ${T.fontSans}`, color: T.ink, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      {label}
      {hint}
    </label>
  );
}

function FieldCard({
  field, result, groupIndex, groupSize, canRevert, confirmedFieldsets, siteUsers,
  selected, expanded, onToggleExpanded, cardRef,
  sameNameIds, onMergeSameName, onSplit, onFormula,
  onRename, onHelpText, onPatch, onType, onFieldset, onMove, onMoveTo, onDelete, onRevert, onJumpToSource,
}) {
  const errors = result?.errors || [];
  const warnings = result?.warnings || [];
  const prov = PROVENANCE[field.provenance] || PROVENANCE.ai;
  const showConfidence = field.provenance !== 'added' && field.confidence;

  return (
    <div
      ref={cardRef}
      style={{
        border: `1px solid ${selected ? T.brand : errors.length ? T.badge.red : T.borderSubtle}`,
        boxShadow: selected ? T.focusRing : 'none',
        borderRadius: T.radiusCard, background: T.white, padding: '10px 12px', marginBottom: 8,
        display: 'flex', gap: 10,
      }}
    >
      {/* Reorder rail — typed position number + up/down buttons, not drag
          (Decision #17) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2, alignItems: 'center' }}>
        <PositionInput index={groupIndex} size={groupSize} onMoveTo={onMoveTo} />
        <button className="fmx-btn-xs" title="Move up" disabled={groupIndex === 0} onClick={() => onMove(-1)}
          style={{ padding: '2px 6px', opacity: groupIndex === 0 ? 0.35 : 1 }}>
          <i className="fa-solid fa-chevron-up" />
        </button>
        <button className="fmx-btn-xs" title="Move down" disabled={groupIndex === groupSize - 1} onClick={() => onMove(1)}
          style={{ padding: '2px 6px', opacity: groupIndex === groupSize - 1 ? 0.35 : 1 }}>
          <i className="fa-solid fa-chevron-down" />
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Label row: click the label to rename it (inline, no modal) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <InlineText
            value={field.name}
            placeholder="Unnamed field — click to name it"
            onCommit={onRename}
            textStyle={{ font: `600 13px/19px ${T.fontSans}`, color: T.ink }}
          />
          {field.isRequired && <span style={{ color: T.red500, fontWeight: 700 }}>*</span>}
          <span style={{ flex: 1 }} />
          <span style={{ font: `600 10px/14px ${T.fontSans}`, color: prov.fg, background: prov.bg, borderRadius: T.radiusPill, padding: '1px 8px' }}>
            {prov.label}
          </span>
          {showConfidence && <Badge color={CONF_BADGE[field.confidence] || 'gray'}>{field.confidence}</Badge>}
          {errors.length > 0
            ? <Badge color="red" icon="circle-exclamation">{errors.length}</Badge>
            : warnings.length > 0
              ? <Badge color="orange" icon="triangle-exclamation">{warnings.length}</Badge>
              : <Badge color="green" icon="check">ok</Badge>}
        </div>

        {/* The rendered control — swaps immediately on type change */}
        <div style={{ margin: '8px 0 0' }}>
          <FieldControlPreview field={field} siteUsers={siteUsers} />
        </div>

        {/* Help text, inline-editable */}
        <div style={{ marginTop: 6 }}>
          <InlineText
            value={field.description || ''}
            placeholder="Add help text…"
            multiline
            onCommit={text => onHelpText(text.trim() || null)}
            textStyle={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, fontStyle: 'italic' }}
            inputStyle={{ maxWidth: 480 }}
          />
        </div>

        {/* Scope: which request types actually asked this question (rule R2).
            A merged field says so here, so "one field, several sections" is
            visible rather than implied. */}
        {(field.scopeOverride?.requestTypeIDs?.length > 0 || field.mergedSections?.length > 1) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <i className="fa-solid fa-diagram-project" style={{ fontSize: 10, color: T.inkSoft }} />
            <span style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkMuted }}>
              {field.scopeOverride?.requestTypeIDs?.length
                ? <>Created on <b>{field.scopeOverride.requestTypeIDs.map(r => r.name || r.id).join(', ')}</b></>
                : <>Asked in {field.mergedSections.length} sections</>}
            </span>
            {field.mergedFrom?.length > 1 && (
              <>
                <Badge color="blueLight">merged ×{field.mergedFrom.length}</Badge>
                <button className="fmx-btn-xs" title={`Merged from: ${field.mergedSections.join(', ')}. Split to keep only the first.`} onClick={onSplit} style={{ padding: '2px 6px' }}>
                  Split
                </button>
              </>
            )}
          </div>
        )}

        {/* Visibility formula (rules R4-R6). FMX's API cannot set this, so the
            formula is generated here and pasted into FMX by hand after the
            push — hence the copy button rather than a silent payload field. */}
        {(field.visibilityFormula || field.visibilityRule) && (
          <div style={{ marginTop: 8, padding: '8px 10px', background: T.tint.info, border: `1px solid ${T.badge.blueLight}`, borderRadius: T.radiusBtn }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
              <i className="fa-solid fa-eye" style={{ fontSize: 11, color: T.brand }} />
              <span style={{ font: `700 11px/16px ${T.fontSans}`, color: T.brand }}>Only shown when</span>
              {field.visibilityRule?.triggerName && (
                <span style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkMuted }}>
                  “{field.visibilityRule.triggerName}” is answered
                </span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ font: `400 10px/14px ${T.fontSans}`, color: T.inkSoft }}>
                Applied by hand in FMX — the API can’t set it
              </span>
            </div>
            {field.visibilityFormula ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  className="fmx-input"
                  value={field.visibilityFormula}
                  onChange={e => onFormula(e.target.value)}
                  spellCheck={false}
                  style={{ flex: 1, font: `400 12px/18px ${T.fontData}`, minWidth: 0 }}
                />
                <CopyFormulaButton text={field.visibilityFormula} label="Copy" />
              </div>
            ) : (
              <div style={{ font: `400 11px/16px ${T.fontSans}`, color: T.orangeText }}>
                <i className="fa-solid fa-triangle-exclamation" />{' '}
                {field.visibilityRule?.error || 'This field is conditional, but the condition couldn’t be built automatically — set its visibility by hand in FMX.'}
              </div>
            )}
            {field.visibilityRule?.rawText && (
              <div style={{ font: `400 10px/14px ${T.fontSans}`, color: T.inkSoft, marginTop: 3 }}>
                From the document: “{field.visibilityRule.rawText}”
              </div>
            )}
          </div>
        )}

        {/* Validation, anchored inline on the field — not in a modal */}
        {errors.map((e, i) => (
          <div key={`e${i}`} style={{ font: `400 12px/17px ${T.fontSans}`, color: T.red500, marginTop: 4 }}>
            <i className="fa-solid fa-circle-exclamation" /> {e}
          </div>
        ))}
        {warnings.map((w, i) => (
          <div key={`w${i}`} style={{ font: `400 12px/17px ${T.fontSans}`, color: T.orangeText, marginTop: 4 }}>
            <i className="fa-solid fa-triangle-exclamation" /> {w}
          </div>
        ))}

        {/* Footer actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button className="fmx-btn-xs" onClick={onToggleExpanded}>
            <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'}`} /> {expanded ? 'Hide settings' : 'Type & settings'}
          </button>
          {/* The remedy the duplicate-name error names, one click away. */}
          {sameNameIds?.length > 0 && (
            <button
              className="fmx-btn-xs-rule"
              title="Combine these into one field, scoped to every request type that asks it"
              onClick={onMergeSameName}
            >
              <i className="fa-solid fa-object-group" /> Merge with {sameNameIds.length} other{sameNameIds.length > 1 ? 's' : ''} named “{field.name}”
            </button>
          )}
          {(field.sourcePage != null || field.sourceItemId) && (
            <button className="fmx-btn-xs" onClick={onJumpToSource}>
              <i className="fa-solid fa-file-lines" /> Source{field.sourcePage != null ? ` · p.${field.sourcePage}` : ''}
            </button>
          )}
          {canRevert && (
            <button className="fmx-btn-xs" title="Copy the original extracted values back from the snapshot" onClick={onRevert}>
              <i className="fa-solid fa-rotate-left" /> Revert to extracted
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button className="fmx-btn-xs" title="Remove this field from the draft" onClick={onDelete} style={{ color: T.red500 }}>
            <i className="fa-solid fa-trash-can" /> Delete
          </button>
        </div>

        {/* Expanded settings */}
        {expanded && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: T.bg, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn }}>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ font: `600 11px/16px ${T.fontSans}`, color: T.inkMuted }}>Field type</span>
                  <PermanentChip />
                </div>
                <select className="fmx-select" style={{ width: 190 }} value={field.fieldType} onChange={e => onType(e.target.value)}>
                  {FIELD_TYPES.map(t => <option key={t.post} value={t.post}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ font: `600 11px/16px ${T.fontSans}`, color: T.inkMuted }}>Fieldset</span>
                  <PermanentChip />
                </div>
                <select className="fmx-select" style={{ width: 220 }} value={normalizeFieldset(field.fieldset) || field.fieldset || ''} onChange={e => onFieldset(e.target.value)}>
                  {confirmedFieldsets.map(fs => <option key={fs} value={fs}>{fieldsetLabel(fs)}</option>)}
                  {!confirmedFieldsets.includes(normalizeFieldset(field.fieldset)) && (
                    <option value={normalizeFieldset(field.fieldset) || field.fieldset}>{fieldsetLabel(field.fieldset)} (not confirmed)</option>
                  )}
                </select>
              </div>
            </div>
            <div style={{ font: `400 11px/15px ${T.fontSans}`, color: T.orangeText, margin: '6px 0 10px' }}>
              You can’t change these in FMX after the field is created — you’d have to delete and recreate it.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
              <Toggle checked={field.isRequired} onChange={v => onPatch({ isRequired: v })} label="Required" hint={<EditableLaterHint />} />
              {field.fieldType === 'text' && (
                <Toggle checked={field.allowMultipleLines} onChange={v => onPatch({ allowMultipleLines: v })} label="Allow multiple lines" hint={<EditableLaterHint />} />
              )}
              {field.fieldType === 'dropDownList' && (
                <>
                  <Toggle checked={field.allowOtherOption} onChange={v => onPatch({ allowOtherOption: v })} label="Allow “Other” option" hint={<EditableLaterHint />} />
                  <Toggle
                    checked={field.allowMultipleSelections}
                    onChange={v => onPatch({
                      allowMultipleSelections: v,
                      // Collapsing to single-select keeps at most one default.
                      ...(v ? {} : { defaults: (field.defaults || []).slice(0, 1) }),
                    })}
                    label="Allow multiple selections"
                    hint={<EditableLaterHint />}
                  />
                </>
              )}
              {(field.fieldType === 'number' || field.fieldType === 'currency') && (
                <Toggle checked={field.disallowNegativeValues} onChange={v => onPatch({ disallowNegativeValues: v })} label="Disallow negative values" hint={<EditableLaterHint />} />
              )}
            </div>

            {field.fieldType === 'dropDownList' && (
              <div style={{ marginBottom: 10 }}>
                <OptionsEditor field={field} onChange={onPatch} />
              </div>
            )}

            {field.rationale && (
              <div style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkMuted }}>
                <b>Why the AI proposed this:</b> {field.rationale}
                {field.sourceItemId && <> · from item <span style={{ fontFamily: T.fontData }}>{field.sourceItemId}</span></>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── The builder screen ──────────────────────────────────────────────────────

export default function FormBuilder({
  session, draft, stage1, stage2, validation, siteUrl, siteUsers,
  confirmedFieldsets, existingFieldsByFieldset, postOptionsByFieldset,
  sourceDoc, onLoadSource,
  saveState, onDraftChange,
  onContinue, continueLoading, onBack,
}) {
  const [railTab, setRailTab] = useState('buckets');   // 'buckets' | 'source'
  const [bucketTab, setBucketTab] = useState(null);    // null = panel picks the first non-empty
  const [sourceFocus, setSourceFocus] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const cardRefs = useRef(new Map());

  const fields = useMemo(() => draft?.fields || [], [draft]);
  const apply = (fn) => onDraftChange(fn(draft));

  // Live names per prerequisite kind (request types, equipment types, …)
  // from the fetched post-options — lets prereq copy say "already exists"
  // vs "create it first" deterministically.
  const liveNames = useMemo(() => liveNamesByPrereqKind(postOptionsByFieldset), [postOptionsByFieldset]);

  const addFieldTo = (fieldset) => {
    const next = addField(draft, fieldset);
    const newId = next.fields[next.fields.length - 1]?.id;
    onDraftChange(next);
    // Open the blank field for editing right away.
    if (newId) setExpandedIds(prev => new Set(prev).add(newId));
  };

  // Groups: every confirmed fieldset (scope order) renders even when empty —
  // that's where "Add field" and bucket promotions land. Fields pointing at
  // an unconfirmed fieldset (validation already flags them) get their own
  // trailing group so nothing is invisible.
  const groups = useMemo(() => {
    const known = new Set(confirmedFieldsets.map(normalizeFieldset));
    const extras = [...new Set(
      fields.map(f => normalizeFieldset(f.fieldset) || f.fieldset).filter(fs => !known.has(fs))
    )];
    return [...confirmedFieldsets, ...extras].map(fs => ({ fieldset: fs, fields: fieldsInFieldset(draft, fs) }));
  }, [draft, fields, confirmedFieldsets]);

  // Same-name collisions within the batch — drives the one-click Merge on each
  // colliding card (the remedy the validation error names).
  const collisions = useMemo(() => findBatchNameCollisions(fields), [fields]);

  // Deterministic duplicates for the Duplicates bucket — the same screen the
  // validator runs, surfaced as a browsable list.
  const duplicateFields = useMemo(() => {
    const out = [];
    for (const f of fields) {
      const existing = existingFieldsByFieldset?.[normalizeFieldset(f.fieldset)] || [];
      const r = screenAgainstExisting(f.name, existing);
      if (!r.verdict) continue;
      out.push({
        field: f,
        kind: r.verdict,
        message: r.verdict === 'duplicate'
          ? `“${r.matches[0].name}” already exists on this site — skip it or rename this one.`
          : `Similar existing field on this site: “${r.matches[0].name}”. Review before creating.`,
      });
    }
    return out;
  }, [fields, existingFieldsByFieldset]);

  const jumpToField = (fieldId) => {
    setSelectedId(fieldId);
    setExpandedIds(prev => new Set(prev).add(fieldId));
    cardRefs.current.get(fieldId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const jumpToSource = (field) => {
    setRailTab('source');
    setSelectedId(field.id);
    setSourceFocus(f => ({ page: field.sourcePage, label: field.name, seq: (f?.seq || 0) + 1 }));
  };
  const jumpToBucket = (bucket) => {
    setRailTab('buckets');
    if (bucket) setBucketTab(bucket);
  };

  // The needs-attention strip: low confidence, flags, couldn't-interpret,
  // plus anything failing validation — each with a jump link. The reviewer
  // works down the list; nothing blocks.
  const attention = useMemo(() => {
    const items = [];
    for (const f of fields) {
      const errs = validation.get(f.id)?.errors || [];
      if (errs.length) items.push({ text: `“${f.name || 'Unnamed field'}” has ${errs.length} validation error${errs.length > 1 ? 's' : ''}`, onJump: () => jumpToField(f.id) });
    }
    for (const f of fields) {
      if (f.confidence === 'low' && f.provenance !== 'added') {
        items.push({ text: `Low confidence: “${f.name}” — check it against the source`, onJump: () => jumpToField(f.id) });
      }
    }
    // Questions the merge pass refused to combine on its own (rule R7) — a
    // permanent choice, so it's a human's call, not a silent guess.
    for (const c of draft?.mergeConflicts || []) {
      items.push({ text: c.detail, onJump: () => jumpToBucket('conflicts') });
    }
    // Conditional fields whose formula couldn't be derived still need doing by
    // hand — say so rather than letting them look finished.
    for (const u of draft?.unresolvedVisibility || []) {
      items.push({ text: `“${u.fieldName}” is conditional but needs its visibility set by hand: ${u.reason}`, onJump: () => jumpToBucket('formulas') });
    }
    for (const e of bucketItems(draft, 'prerequisites')) {
      if (e.status !== 'open') continue;
      const pre = composePrereq(e.item, liveNames);
      // "already exists on this site" needs no attention — only real gaps do.
      if (pre.status !== 'found') items.push({ text: pre.headline, onJump: () => jumpToBucket('prerequisites') });
    }
    for (const e of bucketItems(draft, 'flags')) {
      if (e.status === 'open') items.push({ text: `Needs a decision: ${e.item.issue}`, onJump: () => jumpToBucket('flags') });
    }
    const quarantinedOpen = bucketItems(draft, 'quarantined').filter(e => e.status === 'open').length;
    if (quarantinedOpen) items.push({ text: `${quarantinedOpen} item${quarantinedOpen > 1 ? 's' : ''} couldn’t be interpreted — review or dismiss them`, onJump: () => jumpToBucket('quarantined') });
    return items;
  }, [fields, validation, draft, liveNames]);

  const errorCount = fields.filter(f => (validation.get(f.id)?.errors || []).length > 0).length;

  const saveIndicator = saveState === 'saving'
    ? <span style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}><i className="fa-solid fa-spinner fa-spin" /> Saving…</span>
    : saveState === 'error'
      ? <span style={{ font: `500 12px/17px ${T.fontSans}`, color: T.red500 }}><i className="fa-solid fa-triangle-exclamation" /> Autosave failed — check your connection</span>
      : <span style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft }}><i className="fa-solid fa-check" style={{ color: T.greenDark }} /> Saved</span>;

  return (
    <div>
      {/* Header */}
      <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: '16px 20px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ font: `700 18px/24px ${T.fontSans}`, color: T.ink }}>
              {stage1?.formTitle || session?.source_filename || 'Form builder'}
            </div>
            <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginTop: 3 }}>
              {fields.length} field{fields.length === 1 ? '' : 's'} for <b>{siteUrl}</b>
              {errorCount > 0 && <> · <span style={{ color: T.red500 }}>{errorCount} failing validation (held back at dry run)</span></>}
              {' '}· nothing is created until execute — this build ends at the dry run.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {saveIndicator}
            <button className="fmx-btn-nav-back" onClick={onBack}>Sessions</button>
            <Button variant="primary" iconAfter="arrow-right" onClick={onContinue} disabled={continueLoading}>
              {continueLoading ? 'Refreshing live FMX data…' : 'Continue to dry run'}
            </Button>
          </div>
        </div>

        {attention.length > 0 && (
          <div style={{ margin: '12px 0 0', padding: '10px 14px', background: T.tint.warning, border: `1px solid ${T.badge.orange}`, borderRadius: T.radiusCard }}>
            <div style={{ font: `700 12px/17px ${T.fontSans}`, color: T.orangeText, marginBottom: 4 }}>
              <i className="fa-solid fa-triangle-exclamation" /> Needs your attention ({attention.length})
            </div>
            {attention.map((a, i) => (
              <div key={i} style={{ font: `400 12px/18px ${T.fontSans}`, color: T.ink, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1 }}>{a.text}</span>
                <button
                  onClick={a.onJump}
                  style={{ font: `600 11px/16px ${T.fontSans}`, color: T.brand, background: 'none', border: 'none', cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' }}
                >
                  Jump <i className="fa-solid fa-arrow-right" style={{ fontSize: 9 }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Canvas + right rail */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          {groups.map(({ fieldset, fields: groupFields }) => (
            <div key={fieldset} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px 2px' }}>
                <span style={{ font: `700 13px/19px ${T.fontSans}`, color: T.navy, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>
                  {fieldsetLabel(fieldset)}
                </span>
                <span style={{ font: `400 11px/16px ${T.fontSans}`, color: T.inkSoft }}>
                  {groupFields.length} field{groupFields.length === 1 ? '' : 's'}
                </span>
                <span style={{ flex: 1, borderBottom: `1px solid ${T.borderSubtle}` }} />
                <button className="fmx-btn-xs" onClick={() => addFieldTo(fieldset)}>
                  <i className="fa-solid fa-plus" /> Add field
                </button>
              </div>

              {groupFields.length === 0 && (
                <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft, padding: '4px 2px 2px' }}>
                  No fields in this fieldset yet — add one, or promote an item from the buckets panel.
                </div>
              )}

              {groupFields.map((f, i) => (
                <FieldCard
                  key={f.id}
                  field={f}
                  result={validation.get(f.id)}
                  groupIndex={i}
                  groupSize={groupFields.length}
                  canRevert={f.provenance === 'edited' && !!extractedVersionOf(f, stage2)}
                  confirmedFieldsets={confirmedFieldsets}
                  siteUsers={siteUsers}
                  selected={selectedId === f.id}
                  expanded={expandedIds.has(f.id)}
                  onToggleExpanded={() => setExpandedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                    return next;
                  })}
                  cardRef={el => { if (el) cardRefs.current.set(f.id, el); else cardRefs.current.delete(f.id); }}
                  onRename={name => apply(d => updateField(d, f.id, { name: name.trim() }))}
                  onHelpText={description => apply(d => updateField(d, f.id, { description }))}
                  onPatch={patch => apply(d => updateField(d, f.id, patch))}
                  onType={t => apply(d => changeFieldType(d, f.id, t))}
                  onFieldset={fs => apply(d => changeFieldset(d, f.id, fs))}
                  onMove={dir => apply(d => moveField(d, f.id, dir))}
                  onMoveTo={pos => apply(d => moveFieldToPosition(d, f.id, pos))}
                  sameNameIds={collisions.get(f.id) || []}
                  onMergeSameName={() => apply(d => mergeFieldsInto(d, f.id, [f.id, ...(collisions.get(f.id) || [])]))}
                  onSplit={() => apply(d => splitField(d, f.id))}
                  onFormula={text => apply(d => setVisibilityFormula(d, f.id, text))}
                  onDelete={() => apply(d => deleteField(d, f.id))}
                  onRevert={() => apply(d => revertField(d, f.id, stage2))}
                  onJumpToSource={() => jumpToSource(f)}
                />
              ))}

              {/* Bottom add — same action as the header button, where the
                  eye lands after reviewing the group's last field. */}
              {groupFields.length > 0 && (
                <button
                  onClick={() => addFieldTo(fieldset)}
                  style={{
                    width: '100%', padding: '9px 0', cursor: 'pointer',
                    background: 'none', border: `1px dashed ${T.border}`, borderRadius: T.radiusCard,
                    font: `600 12px/17px ${T.fontSans}`, color: T.inkMuted,
                  }}
                >
                  <i className="fa-solid fa-plus" /> Add field to {fieldsetLabel(fieldset)}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Right rail: buckets / source */}
        <div style={{ width: 400, flexShrink: 0, position: 'sticky', top: 12, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${T.borderSubtle}` }}>
            {[
              { id: 'buckets', label: 'Review buckets', icon: 'inbox' },
              { id: 'source', label: 'Source document', icon: 'file-lines' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setRailTab(t.id)}
                style={{
                  flex: 1, padding: '9px 8px', cursor: 'pointer', border: 'none',
                  borderBottom: `2px solid ${railTab === t.id ? T.brand : 'transparent'}`,
                  background: railTab === t.id ? T.white : T.bg,
                  font: `600 12px/17px ${T.fontSans}`, color: railTab === t.id ? T.brand : T.inkMuted,
                }}
              >
                <i className={`fa-solid fa-${t.icon}`} /> {t.label}
              </button>
            ))}
          </div>
          {railTab === 'buckets' ? (
            <BucketsPanel
              draft={draft}
              duplicateFields={duplicateFields}
              confirmedFieldsets={confirmedFieldsets}
              liveNames={liveNames}
              tab={bucketTab}
              onTab={setBucketTab}
              onDismiss={(bucket, key, reason, restore) => apply(d => restore
                ? restoreBucketItem(d, bucket, key)
                : dismissBucketItem(d, bucket, key, reason))}
              onPromote={(bucket, key, item, fieldset) => apply(d => promoteBucketItem(d, bucket, key, item, fieldset))}
              onJumpToField={jumpToField}
              onJumpToFieldName={name => {
                const match = fields.find(f => f.name === name);
                if (match) jumpToField(match.id);
              }}
              onDeleteField={fieldId => apply(d => deleteField(d, fieldId))}
            />
          ) : (
            <SourcePanel sourceDoc={sourceDoc} onLoad={onLoadSource} focus={sourceFocus} />
          )}
        </div>
      </div>
    </div>
  );
}
